import { Button, Form, notification, Row, Switch, Typography } from "antd";
import dayjs from "dayjs";
import { useDistributionPoints } from "hooks/distributionPoints/useDistributionPoints";
import { useEffect, useState } from "react";
import ResponsiveCol from "common/components/form/ResponsiveCol";
import StockControlTable from "./components/StockControlTable";
import { useDateRange } from "context/DateRangeContext";
import { useStockControl } from "hooks/stock/useStockControl";
import StockControlTableByPeriod from "./components/StockControlTableByPeriod";
import { FileExcelOutlined } from "@ant-design/icons";
import moment from "moment";
import {
  getAllEquipmentsByApplicationType,
  ResponseInputEquipments,
} from "api/generated";
import { useClient } from "hooks/useClient";
import ManualVolumeRegisterDrawer from "routes/volumeMeasurerRegistries/components/ManualVolumeRegistrerDrawer";
import {
  DateFilterType,
  DateRangePicker,
} from "common/components/widgets/datePicker/DateRangePicker";

function StockControl() {
  const { distributionPoint } = useDistributionPoints();
  const [showByDay, setShowByDay] = useState(true);

  // ESTADO PARA MEDICIONES MANUALES
  const [loadingEquipment, setLoadingEquipments] = useState(false);
  const [equipments, setEquipments] = useState<ResponseInputEquipments[]>([]);
  const [showAddMesurement, setShowAddMesurement] = useState(false);
  const [measurementDate, setMeasurementDate] = useState<moment.Moment>();
  const { client } = useClient();

  const { rangeTimeValue, setRangeTimeValue, getFormattedDates } =
    useDateRange();
  const { startDate, endDate } = getFormattedDates();
  const {
    registries,
    registriesByPeriod,
    currentPage,
    pageSize,
    totalItems,
    loading,
    error,
    setPage,
    updatePageSize,
    recalculateGic,
    recalculatePeriod,
    upsertCauseAndSolutionOnDifference,
    getLastMeasurement,
    downloadStockControl,
  } = useStockControl({
    startDate,
    endDate,
    fetchByDay: showByDay,
  });

  /**
   * Maneja el cambio de estado del Switch entre vista por día o por período
   * @param {boolean} checked - Indica si el switch está activado (vista por día) o desactivado (vista por período)
   */
  function handleSwitchChange(checked: boolean) {
    setShowByDay(checked);
    setPage(1);
    // Cuando cambia a modo día, resetear al mes actual
    if (checked) {
      const newRange: [dayjs.Dayjs, dayjs.Dayjs] = [
        rangeTimeValue[0].startOf("month"),
        rangeTimeValue[0].endOf("month"),
      ];
      setRangeTimeValue(newRange);
    } else {
      updatePageSize(5);
    }
  }

  /**
   * Efecto que muestra una notificación de error cuando ocurre un problema en la carga de datos
   * @param {string} error - Mensaje de error a mostrar en la notificación
   */
  useEffect(() => {
    if (error) {
      notification.error({
        message: error,
      });
    }
  }, [error]);

  function handleRegisterManualMeasurement(dateToRegister: moment.Moment) {
    setLoadingEquipments(true);
    setEquipments([]);
    setMeasurementDate(dateToRegister.clone().endOf("month"));
    getAllEquipmentsByApplicationType({
      client,
      path: {
        distributionPointId: distributionPoint?.id ?? "",
      },
      query: {
        applicationType: "MEDIDOR_NIVEL",
      },
    })
      .then(({ data }) => {
        setEquipments(data ?? []);
        setShowAddMesurement(true);
      })
      .finally(() => {
        setLoadingEquipments(false);
      });
  }

  return (
    <div>
      <Form layout="vertical">
        <Row gutter={24}>
          <ResponsiveCol size="xl">
            <Typography.Title>{distributionPoint?.name}</Typography.Title>
          </ResponsiveCol>
          <ResponsiveCol size="lg">
            <Form.Item label="Periodo de Control de Existencias">
              <DateRangePicker
                rangeTimeValue={rangeTimeValue}
                setRangeTimeValue={setRangeTimeValue}
                config={{
                  allowedTypes: [DateFilterType.MONTH, DateFilterType.RANGE],
                  forceType: showByDay
                    ? DateFilterType.MONTH
                    : DateFilterType.RANGE,
                  defaultType: DateFilterType.MONTH,
                  showQuickFilters: true,
                }}
              />
            </Form.Item>
          </ResponsiveCol>
          <ResponsiveCol size="lg">
            <Switch
              style={{ float: "right" }}
              checkedChildren="Resumen por Día"
              unCheckedChildren="Resumen por Periodo"
              value={showByDay}
              onChange={handleSwitchChange}
            />
          </ResponsiveCol>
        </Row>
        <Row>
          <ResponsiveCol size="xl">
            <Button
              variant="outlined"
              color="blue"
              icon={<FileExcelOutlined />}
              style={{ float: "right" }}
              loading={loading}
              disabled={totalItems === 0 || loading}
              onClick={() => {
                downloadStockControl();
              }}
            >
              Descargar reporte {showByDay ? "diario" : "del periodo"} del
              Control de Existencias
            </Button>
          </ResponsiveCol>
        </Row>
        <Row gutter={24}>
          {showByDay && (
            <StockControlTable
              registries={registries}
              currentPage={currentPage}
              pageSize={pageSize}
              totalItems={totalItems}
              loading={loading || loadingEquipment}
              onPageChange={setPage}
              onPageSizeChange={updatePageSize}
              recalculateGic={recalculateGic}
              upsertCauseAndSolutionOnDifference={
                upsertCauseAndSolutionOnDifference
              }
              getLastMeasurement={getLastMeasurement}
              onRegisterManualMeasurement={handleRegisterManualMeasurement}
            />
          )}
          {!showByDay && (
            <StockControlTableByPeriod
              registries={registriesByPeriod}
              currentPage={currentPage}
              pageSize={pageSize}
              totalItems={totalItems}
              loading={loading || loadingEquipment}
              onPageChange={setPage}
              onPageSizeChange={updatePageSize}
              recalculatePeriod={recalculatePeriod}
              onRegisterManualMeasurement={handleRegisterManualMeasurement}
            />
          )}
        </Row>
      </Form>
      {measurementDate && (
        <ManualVolumeRegisterDrawer
          isVisible={showAddMesurement}
          onClose={(update) => {
            if (update) {
              if (showByDay) {
                recalculateGic(measurementDate);
              } else {
                recalculatePeriod(measurementDate);
              }
            }
            setShowAddMesurement(false);
          }}
          measurementEquipments={equipments}
          defaultDate={dayjs(measurementDate.valueOf())}
        />
      )}
    </div>
  );
}
export default StockControl;
